import { AddIcon, DownloadIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Box,
  Center,
  Flex,
  IconButton,
  Progress,
  Spinner,
  Text
} from "@chakra-ui/react";
import {
  Background,
  Controls,
  MarkerType,
  ReactFlow,
  ReactFlowInstance,
  type Connection,
  type Edge,
  type Node,
  type NodeMouseHandler
} from "@xyflow/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// @ts-ignore
import "@xyflow/react/dist/style.css";
import { SubscribeFunction } from "hooks/use-stomp-client";
import { Router } from "router-manager/parts";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import { ButtonWithLoading } from "components/mailing-components/mailing-table/icon-button-with-loading";
import { MailingTableRow } from "components/mailing-components/mailing-table";
import { PipelineAction } from "../classes/state-machine/state-control";
import {
  PipelineStatsPayload,
  State,
  StateStatsEvent
} from "../classes/state-machine/state";
import { StateNode, type StateNodeData } from "./StateNode";
import { DeletableEdge } from "./DeletableEdge";
import { StatesMachine } from "../classes/state-machine/state-machine";
import {
  addOperation,
  canInsertOperation,
  connectStatesIfAllowed,
  disconnectEdges,
  disconnectStates,
  getVisibleStateIdForInsertedOperation,
  parseEdgeIdsFromDeletableEdgeId
} from "../pipeline-commands";
import {
  getDefaultNodeLabel,
  getTransitionEdgeColor,
  PIPELINE_DRAG_TYPE,
  SUPPORTS_MULTIPLE_EXECUTORS
} from "../node-catalog";
import { PipelineEndpoints } from "../utils";
import { Toast } from "components/toast";
import { PipelineTemplateGallery } from "../template-gallery";
import { PipelineModeloDTO } from "components/modelos-pipeline-components/utils";

const HORIZONTAL_GAP = 350;
const VERTICAL_SPACING = 200;
const TOP_Y = 160;
const NODE_COLLISION_HEIGHT = 120;
const boxShadow = "sm";

interface GraphInfo {
  depths: Map<string, number>;
  successors: Map<string, Set<string>>;
  predecessors: Map<string, Set<string>>;
}

/**
 * Compute the topological depth (column index) of each visible state using
 * the longest path from source nodes (Kahn's algorithm). Nodes at depth 0
 * appear on the left; each subsequent depth moves one column to the right.
 * Also returns the adjacency graph for barycenter ordering.
 */
function computeGraphLayout(
  visibleStates: State[],
  pipelineData: StatesMachine
): GraphInfo {
  const visibleIds = new Set(visibleStates.map((s) => String(s.id)));

  const successors = new Map<string, Set<string>>();
  const predecessors = new Map<string, Set<string>>();
  const predecessorCount = new Map<string, number>();

  visibleIds.forEach((id) => {
    successors.set(id, new Set());
    predecessors.set(id, new Set());
    predecessorCount.set(id, 0);
  });

  pipelineData.states.forEach((state) => {
    (state.transitions ?? []).forEach((transition) => {
      if (transition.event === "ERROR") return;

      const src = String(pipelineData.getVisibleStateId(state.id));
      const tgt = String(pipelineData.getVisibleStateId(transition.target));

      if (src === tgt || !visibleIds.has(src) || !visibleIds.has(tgt)) return;
      if (successors.get(src)!.has(tgt)) return;

      successors.get(src)!.add(tgt);
      predecessors.get(tgt)!.add(src);
      predecessorCount.set(tgt, (predecessorCount.get(tgt) ?? 0) + 1);
    });
  });

  const depths = new Map<string, number>();
  const inDegree = new Map(predecessorCount);

  const queue: string[] = [];
  visibleIds.forEach((id) => {
    depths.set(id, 0);
    if (inDegree.get(id) === 0) queue.push(id);
  });

  while (queue.length) {
    const current = queue.shift()!;
    const currentDepth = depths.get(current)!;

    successors.get(current)!.forEach((neighbor) => {
      const newDepth = currentDepth + 1;
      if (newDepth > depths.get(neighbor)!) {
        depths.set(neighbor, newDepth);
      }
      const remaining = inDegree.get(neighbor)! - 1;
      inDegree.set(neighbor, remaining);
      if (remaining === 0) queue.push(neighbor);
    });
  }

  let maxDepth = 0;
  depths.forEach((d) => {
    if (d > maxDepth) maxDepth = d;
  });

  visibleIds.forEach((id) => {
    if (inDegree.get(id)! > 0) depths.set(id, maxDepth + 1);
  });

  return { depths, successors, predecessors };
}

/**
 * Order nodes within each column using barycenter heuristic to minimize
 * edge crossings. Nodes are sorted by the average Y-position of their
 * connected predecessors in the previous column.
 */
function orderColumnsByBarycenter(
  statesByDepth: Map<number, string[]>,
  graph: GraphInfo,
  maxDepth: number
): void {
  // First column (depth 0) keeps its natural order.
  // For each subsequent column, compute the barycenter from predecessor positions.
  const positionInColumn = new Map<string, number>();

  // Assign initial indices for depth 0
  const col0 = statesByDepth.get(0) ?? [];
  col0.forEach((id, i) => positionInColumn.set(id, i));

  for (let d = 1; d <= maxDepth; d++) {
    const column = statesByDepth.get(d);
    if (!column || column.length <= 1) {
      column?.forEach((id, i) => positionInColumn.set(id, i));
      continue;
    }

    const barycenters = new Map<string, number>();

    column.forEach((nodeId) => {
      const preds = graph.predecessors.get(nodeId);
      if (!preds || preds.size === 0) {
        barycenters.set(nodeId, Infinity);
        return;
      }

      let sum = 0;
      let count = 0;
      preds.forEach((predId) => {
        const predPos = positionInColumn.get(predId);
        if (predPos !== undefined) {
          sum += predPos;
          count++;
        }
      });

      barycenters.set(nodeId, count > 0 ? sum / count : Infinity);
    });

    column.sort((a, b) => barycenters.get(a)! - barycenters.get(b)!);
    column.forEach((id, i) => positionInColumn.set(id, i));
  }

  // Second pass: right-to-left refinement using successors
  for (let d = maxDepth - 1; d >= 0; d--) {
    const column = statesByDepth.get(d);
    if (!column || column.length <= 1) continue;

    const barycenters = new Map<string, number>();

    column.forEach((nodeId) => {
      const succs = graph.successors.get(nodeId);
      if (!succs || succs.size === 0) {
        barycenters.set(nodeId, positionInColumn.get(nodeId) ?? 0);
        return;
      }

      let sum = 0;
      let count = 0;
      succs.forEach((succId) => {
        const succPos = positionInColumn.get(succId);
        if (succPos !== undefined) {
          sum += succPos;
          count++;
        }
      });

      barycenters.set(
        nodeId,
        count > 0 ? sum / count : (positionInColumn.get(nodeId) ?? 0)
      );
    });

    column.sort((a, b) => barycenters.get(a)! - barycenters.get(b)!);
    column.forEach((id, i) => positionInColumn.set(id, i));
  }
}

/**
 * Compute hierarchical positions for all nodes at once.
 * - Columns by topological depth (left-to-right)
 * - Barycenter ordering within columns (minimizes edge crossings)
 * - Global vertical centering across all columns
 */
function computeHierarchicalPositions(
  visibleStates: State[],
  pipelineData: StatesMachine
): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();
  if (!visibleStates.length) return positions;

  const graph = computeGraphLayout(visibleStates, pipelineData);

  const statesByDepth = new Map<number, string[]>();
  let maxDepth = 0;

  visibleStates.forEach((state) => {
    const d = graph.depths.get(String(state.id)) ?? 0;
    if (d > maxDepth) maxDepth = d;
    if (!statesByDepth.has(d)) statesByDepth.set(d, []);
    statesByDepth.get(d)!.push(String(state.id));
  });

  // Order nodes within each column to minimize edge crossings
  orderColumnsByBarycenter(statesByDepth, graph, maxDepth);

  // Find the tallest column to compute a global vertical center
  let maxColumnSize = 0;
  statesByDepth.forEach((col) => {
    if (col.length > maxColumnSize) maxColumnSize = col.length;
  });
  const globalCenterY = TOP_Y;

  // Place each column, centered vertically around the global center
  for (let d = 0; d <= maxDepth; d++) {
    const column = statesByDepth.get(d);
    if (!column) continue;

    const totalHeight = (column.length - 1) * VERTICAL_SPACING;
    const startY = globalCenterY - totalHeight / 2;

    column.forEach((nodeId, index) => {
      positions.set(nodeId, {
        x: d * HORIZONTAL_GAP,
        y: startY + index * VERTICAL_SPACING
      });
    });
  }

  return positions;
}

/**
 * Find the closest available position for a single new node,
 * resolving collisions by shifting vertically only (preserving column alignment).
 */
function findColumnAlignedPosition(
  desiredPosition: { x: number; y: number },
  occupiedPositions: Array<{ x: number; y: number }>
): { x: number; y: number } {
  const hasCollision = (candidate: { x: number; y: number }) =>
    occupiedPositions.some(
      (pos) =>
        Math.abs(pos.x - candidate.x) < HORIZONTAL_GAP * 0.8 &&
        Math.abs(pos.y - candidate.y) < NODE_COLLISION_HEIGHT
    );

  if (!hasCollision(desiredPosition)) return desiredPosition;

  // Search vertically only, alternating above/below
  for (let step = 1; step <= 10; step++) {
    const below = {
      x: desiredPosition.x,
      y: desiredPosition.y + step * VERTICAL_SPACING
    };
    if (!hasCollision(below)) return below;

    const above = {
      x: desiredPosition.x,
      y: desiredPosition.y - step * VERTICAL_SPACING
    };
    if (!hasCollision(above)) return above;
  }

  return {
    x: desiredPosition.x,
    y: desiredPosition.y + 11 * VERTICAL_SPACING
  };
}

export function Fluxogram({
  pipelineData,
  isLoading,
  subscribe,
  dispatchPipelineData,
  isWsConnected,
  disconnect,
  selectedStateId,
  onSelectState,
  isEditable,
  isSaving,
  setIsSaving,
  mailingData,
  handleCreatePipeline,
  isShowPipelineTemplate,
  setPermitedShowTemplate
}: {
  pipelineData: StatesMachine | undefined;
  isLoading: boolean;
  subscribe: SubscribeFunction;
  dispatchPipelineData: (value: PipelineAction) => void;
  isWsConnected: boolean;
  disconnect: () => void;
  selectedStateId?: number;
  onSelectState?: (stateId: number) => void;
  isEditable: boolean;
  isSaving?: boolean;
  setIsSaving?: (isSaving: boolean) => void;
  mailingData: Partial<MailingTableRow>;
  handleCreatePipeline: () => Promise<void>;
  setPermitedShowTemplate: React.Dispatch<React.SetStateAction<boolean>>;
  isShowPipelineTemplate: boolean;
}) {
  const reactFlowInstanceRef = useRef<ReactFlowInstance<Node, Edge> | null>(
    null
  );
  const [nodePositions, setNodePositions] = useState<
    Record<string, { x: number; y: number }>
  >({});
  const nodePositionsRef = useRef<Record<string, { x: number; y: number }>>({});
  const positionsSyncFrameRef = useRef<number | null>(null);
  const hasFittedRef = useRef(false);

  const syncNodePositionsToState = useCallback((immediate = false) => {
    if (immediate) {
      if (positionsSyncFrameRef.current !== null) {
        cancelAnimationFrame(positionsSyncFrameRef.current);
        positionsSyncFrameRef.current = null;
      }

      setNodePositions({ ...nodePositionsRef.current });
      return;
    }

    if (positionsSyncFrameRef.current !== null) return;

    positionsSyncFrameRef.current = requestAnimationFrame(() => {
      positionsSyncFrameRef.current = null;
      setNodePositions({ ...nodePositionsRef.current });
    });
  }, []);

  const updateNodePosition = useCallback(
    (nodeId: string, position: Node["position"], immediate = false) => {
      const previousPosition = nodePositionsRef.current[nodeId];

      if (
        previousPosition?.x === position.x &&
        previousPosition?.y === position.y
      ) {
        return;
      }

      nodePositionsRef.current[nodeId] = position;
      syncNodePositionsToState(immediate);
    },
    [syncNodePositionsToState]
  );

  const nodeTypes = useMemo(() => ({ stateNode: StateNode }), []);
  const edgeTypes = useMemo(() => ({ deletable: DeletableEdge }), []);

  const visibleStates = useMemo(() => {
    return pipelineData?.getVisibleStates() ?? [];
  }, [pipelineData]);

  useEffect(() => {
    return () => {
      if (positionsSyncFrameRef.current !== null) {
        cancelAnimationFrame(positionsSyncFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!visibleStates.length) {
      if (Object.keys(nodePositionsRef.current).length) {
        nodePositionsRef.current = {};
        syncNodePositionsToState(true);
      }
      return;
    }

    const nextPositions = { ...nodePositionsRef.current };
    let changed = false;
    const visibleStateIds = new Set(
      visibleStates.map((state) => String(state.id))
    );

    let removedCount = 0;
    let addedCount = 0;

    Object.keys(nextPositions).forEach((positionedStateId) => {
      if (visibleStateIds.has(positionedStateId)) return;

      delete nextPositions[positionedStateId];
      changed = true;
      removedCount++;
    });

    const newStateIds = visibleStates
      .filter((state) => !nextPositions[String(state.id)])
      .map((state) => String(state.id));

    if (newStateIds.length === 0 && !changed) return;

    // When all (or most) nodes need positioning, use hierarchical layout
    // for optimal placement. Otherwise, place incrementally.
    const needsFullLayout =
      newStateIds.length === visibleStates.length ||
      (removedCount > 0 && newStateIds.length > 0);

    if (needsFullLayout && pipelineData) {
      // Full hierarchical layout: compute all positions at once with
      // barycenter ordering to minimize edge crossings.
      const hierarchicalPositions = computeHierarchicalPositions(
        visibleStates,
        pipelineData
      );

      hierarchicalPositions.forEach((pos, nodeId) => {
        nextPositions[nodeId] = pos;
      });

      changed = true;
      addedCount = newStateIds.length;
    } else if (newStateIds.length > 0) {
      // Incremental layout: place new nodes using graph-aware positions
      // while respecting existing node positions.
      const graph = pipelineData
        ? computeGraphLayout(visibleStates, pipelineData)
        : null;

      newStateIds.forEach((key, fallbackIndex) => {
        let desiredPosition: { x: number; y: number };

        if (graph) {
          const d = graph.depths.get(key) ?? 0;

          // Try to align with connected predecessors' Y positions
          const preds = graph.predecessors.get(key);
          let targetY = TOP_Y;

          if (preds && preds.size > 0) {
            let sumY = 0;
            let count = 0;
            preds.forEach((predId) => {
              const predPos = nextPositions[predId];
              if (predPos) {
                sumY += predPos.y;
                count++;
              }
            });
            if (count > 0) targetY = sumY / count;
          }

          desiredPosition = { x: d * HORIZONTAL_GAP, y: targetY };
        } else {
          desiredPosition = {
            x:
              (Object.keys(nextPositions).length + fallbackIndex) *
              HORIZONTAL_GAP,
            y: TOP_Y
          };
        }

        const occupiedPositions = Object.entries(nextPositions)
          .filter(([stateId]) => stateId !== key)
          .map(([, position]) => position);

        nextPositions[key] = findColumnAlignedPosition(
          desiredPosition,
          occupiedPositions
        );

        changed = true;
        addedCount++;
      });
    }

    if (!changed) return;

    // When state IDs are replaced (e.g. after backend assigns new IDs),
    // positions are rebuilt but node count stays the same, so fitView
    // won't trigger via the nodeCountChanged check. Reset the flag so
    // the viewport re-centers on the new layout.
    if (removedCount > 0 && addedCount > 0) {
      hasFittedRef.current = false;
    }

    nodePositionsRef.current = nextPositions;
    syncNodePositionsToState(true);
  }, [syncNodePositionsToState, visibleStates, pipelineData]);

  const getStateStats = useCallback(
    (state: State) => {
      if (!pipelineData) return undefined;

      if (
        SUPPORTS_MULTIPLE_EXECUTORS.includes(state.operation) &&
        state.stateType === "NOOP"
      ) {
        const relatedStates = pipelineData.states.filter(
          (candidate) =>
            candidate.operation === state.operation &&
            candidate.stateType === "EXECUTOR"
        );

        const eventsMap = new Map<string, StateStatsEvent>();
        let finished = false;

        relatedStates.forEach((relatedState) => {
          const stats = pipelineData.statusByState.get(relatedState.id);
          if (!stats) return;

          finished = finished || stats.finished;

          stats.events.forEach((event) => {
            const key = `${event.code}-${event.statusDescription}`;
            const current = eventsMap.get(key);
            if (!current) {
              eventsMap.set(key, { ...event });
              return;
            }

            eventsMap.set(key, {
              ...current,
              total: current.total + event.total
            });
          });
        });

        if (!eventsMap.size) return undefined;

        return { finished, events: [...eventsMap.values()] };
      }

      return pipelineData.statusByState.get(state.id);
    },
    [pipelineData]
  );

  const stateNodes = useMemo<Node[]>(() => {
    if (!pipelineData) return [];

    const pendingConfigByVisibleState =
      pipelineData.getPendingConfigCountByVisibleState();

    return visibleStates.map((state, index) => {
      const stats = getStateStats(state);
      const pendingConfigCount = pendingConfigByVisibleState.get(state.id) ?? 0;
      const position = nodePositions[String(state.id)] ?? {
        x: index * HORIZONTAL_GAP,
        y: TOP_Y
      };

      const canDelete =
        isEditable && !["START", "END"].includes(state.operation);

      const nodeLabel = getDefaultNodeLabel(
        state.operation,
        mailingData?.origem,
        mailingData?.qtdGerada
      );

      const nodeData: StateNodeData = {
        label: nodeLabel.title,
        subtitle: nodeLabel.subtitle,
        stats,
        operation: state.operation,
        isSelected: selectedStateId === state.id,
        pipelineData,
        stateId: state.id,
        isConfigPending: pendingConfigCount > 0,
        pendingConfigCount,
        hasIncomingHandle: state.operation !== "START",
        hasOutgoingHandle: state.operation !== "END",
        onDelete: canDelete
          ? () => {
              dispatchPipelineData({
                type: "MUTATE",
                updater: (draft) => {
                  draft.removeOperation(state.operation);
                }
              });
            }
          : undefined
      };

      return {
        id: String(state.id),
        type: "stateNode",
        position,
        selected: selectedStateId === state.id,
        zIndex: selectedStateId === state.id ? 10 : 2,
        data: nodeData
      };
    });
  }, [
    dispatchPipelineData,
    getStateStats,
    isEditable,
    mailingData?.origem,
    mailingData?.qtdGerada,
    nodePositions,
    pipelineData,
    selectedStateId,
    visibleStates
  ]);

  const hasPendingConfigNodes = useMemo(
    () =>
      stateNodes.some(
        (node) => (node.data as StateNodeData | undefined)?.isConfigPending
      ),
    [stateNodes]
  );

  const progressData = useMemo(() => {
    const total = mailingData?.qtdGerada;
    if (!total || !pipelineData?.states?.length) return null;

    const endState = pipelineData.states.find((s) => s.operation === "END");
    if (!endState) return null;

    // Find the state that has a SUCCESS transition to END (last processing state)
    const lastState = pipelineData.states.find((s) =>
      s.transitions?.some(
        (t) => t.target === endState.id && t.event === "SUCCESS"
      )
    );

    let totalErrors = 0;
    let lastStateSuccess = 0;

    pipelineData.states.forEach((state) => {
      if (state.operation === "START" || state.operation === "END") return;
      const stats = pipelineData.statusByState.get(state.id);
      if (!stats) return;

      stats.events.forEach((event) => {
        if (event.code >= 400) {
          totalErrors += event.total;
        }
      });
    });

    if (lastState) {
      const lastStats = pipelineData.statusByState.get(lastState.id);
      lastStats?.events.forEach((event) => {
        if (event.code === 200) {
          lastStateSuccess += event.total;
        }
      });
    }

    const processed = totalErrors + lastStateSuccess;
    const percent = Math.min((processed / total) * 100, 100);

    return { processed, total, percent };
  }, [mailingData?.qtdGerada, pipelineData]);

  const pipelineId = pipelineData?.id;

  useEffect(() => {
    if (!pipelineId || !isWsConnected) return;

    subscribe(`/user/queue/pipeline-stats/pipeline/${pipelineId}`, (msg) => {
      let body: PipelineStatsPayload;
      try {
        body = JSON.parse(msg.body);
      } catch {
        return;
      }
      if (!body || !body.states || typeof body.states !== "object") return;
      if (body.id !== pipelineId) return;
      requestAnimationFrame(() => {
        dispatchPipelineData({ type: "UPDATE_STATS", payload: body });
      });
      if (body.pipelineFinished === true) {
        disconnect();
      }
    });
  }, [pipelineId, subscribe, dispatchPipelineData, isWsConnected, disconnect]);

  const edges = useMemo<Edge[]>(() => {
    if (!pipelineData) return [];

    const edgeMap = new Map<string, Edge>();

    pipelineData.states.forEach((state) => {
      const transitions = state.transitions ?? [];
      transitions.forEach((transition, index) => {
        if (transition.event === "ERROR") return;

        const visualSource = String(pipelineData.getVisibleStateId(state.id));
        const visualTarget = String(
          pipelineData.getVisibleStateId(transition.target)
        );

        if (visualSource === visualTarget) return;

        const edgeId = `e-${visualSource}-${visualTarget}-${transition.event}-${index}`;
        const dedupeKey = `${visualSource}:${visualTarget}`;
        if (edgeMap.has(dedupeKey)) return;

        edgeMap.set(dedupeKey, {
          id: edgeId,
          source: visualSource,
          target: visualTarget,
          type: "deletable",
          animated: true,
          style: {
            stroke: getTransitionEdgeColor(state.operation),
            strokeDasharray: "6 6",
            strokeWidth: 2
          },
          markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18 },
          data: {
            onDelete: isEditable
              ? (edgeId: string) => {
                  const ids = parseEdgeIdsFromDeletableEdgeId(edgeId);
                  if (!ids) return;

                  dispatchPipelineData({
                    type: "MUTATE",
                    updater: (draft) => {
                      disconnectStates(draft, ids.sourceId, ids.targetId);
                    }
                  });
                }
              : undefined
          }
        });
      });
    });

    return [...edgeMap.values()];
  }, [pipelineData, dispatchPipelineData, isEditable]);

  const prevNodeCountRef = useRef(0);

  useEffect(() => {
    if (!stateNodes.length) return;
    if (!reactFlowInstanceRef.current) return;

    const nodeCountChanged = prevNodeCountRef.current !== stateNodes.length;
    prevNodeCountRef.current = stateNodes.length;

    if (hasFittedRef.current && !nodeCountChanged) return;

    const timer = setTimeout(() => {
      reactFlowInstanceRef.current?.fitView({
        padding: 0.25,
        duration: 280,
        maxZoom: 0.75
      });
      hasFittedRef.current = true;
    }, 60);

    return () => clearTimeout(timer);
  }, [stateNodes]);

  const handleConnect = (connection: Connection) => {
    const sourceId = Number(connection.source);
    const targetId = Number(connection.target);
    if (!sourceId || !targetId || !pipelineData) return;

    const blockedReason = pipelineData.getConnectionBlockedReason(
      sourceId,
      targetId
    );

    if (blockedReason) {
      Toast({ title: blockedReason, status: "warning" });
      return;
    }

    dispatchPipelineData({
      type: "MUTATE",
      updater: (draft) => {
        connectStatesIfAllowed(draft, sourceId, targetId);
      }
    });
  };

  const handleDeleteEdges = (deletedEdges: Edge[]) => {
    if (!deletedEdges.length) return;

    dispatchPipelineData({
      type: "MUTATE",
      updater: (draft) => {
        disconnectEdges(draft, deletedEdges);
      }
    });
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    if (!isEditable) return;

    const operation = event.dataTransfer.getData(
      PIPELINE_DRAG_TYPE
    ) as State["operation"];
    if (!operation || !pipelineData) return;
    if (!canInsertOperation(pipelineData, operation)) return;

    const flowPosition = reactFlowInstanceRef.current?.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY
    });
    if (!flowPosition) return;

    // Calculate the future visible state id before dispatch so the dropped
    // position can be persisted in the same frame as the mutation.
    const visibleCreatedStateId = getVisibleStateIdForInsertedOperation(
      pipelineData,
      operation
    );

    updateNodePosition(String(visibleCreatedStateId), flowPosition, true);

    dispatchPipelineData({
      type: "MUTATE",
      updater: (draft) => {
        addOperation(draft, operation);
      }
    });
  };

  const handleNodeClick: NodeMouseHandler = (_, node) => {
    const stateId = Number(node.id);
    if (!stateId) return;
    onSelectState?.(stateId);
  };

  if (isLoading) {
    return (
      <Center w="100%" h="100%">
        <Spinner />
      </Center>
    );
  }

  if (!pipelineData) {
    return null;
  }

  const selectedState = selectedStateId
    ? pipelineData.getState(selectedStateId)
    : undefined;
  const isPipelineFinished = !!pipelineData.finished;
  const isPipelineCreated = !!pipelineData.id;

  return (
    <Box
      w="100%"
      h="100%"
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = isEditable ? "move" : "none";
      }}
      onDrop={handleDrop}
      sx={{
        ".react-flow__edge-labels": { zIndex: 1 },
        ".react-flow__edge:hover .edge-delete-btn, .react-flow__edge.selected .edge-delete-btn":
          { opacity: "1 !important" },
        ".react-flow__handle": {
          background: "#FFFFFF",
          border: "2px solid #94A3B8",
          width: "12px",
          height: "12px"
        },
        ".react-flow__node": {
          transition: "box-shadow .15s ease"
        },
        ".react-flow__node.selected": {
          boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.25)"
        }
      }}
      pos="relative"
      bg="#F3F6FA"
    >
      {isPipelineFinished && (
        <Box
          position="absolute"
          top="3"
          left="50%"
          transform="translateX(-50%)"
          zIndex={11}
        >
          <Alert
            status="success"
            borderRadius="10px"
            border="1px solid"
            borderColor="#9AE6B4"
            bg="#F0FFF4"
            boxShadow={boxShadow}
            fontWeight="medium"
          >
            <AlertIcon />
            Pipeline finalizado com sucesso.
          </Alert>
        </Box>
      )}

      {progressData && isPipelineCreated && (
        <Box
          position="absolute"
          top="3"
          right="3"
          bg="#FFFFFF"
          borderWidth="1px"
          borderColor="#E8EAED"
          borderRadius="12px"
          px="4"
          py="3"
          zIndex={10}
          minW="220px"
          boxShadow={boxShadow}
        >
          <Flex justify="space-between" align="center" mb="1.5">
            <Text fontSize="xs" fontWeight="700" color="#475569">
              Progresso
            </Text>
            <Text fontSize="xs" fontWeight="800" color="#334155">
              {progressData.processed.toLocaleString("pt-BR")}
              {" / "}
              {progressData.total.toLocaleString("pt-BR")}
            </Text>
          </Flex>
          <Progress
            value={progressData.percent}
            size="sm"
            borderRadius="full"
            colorScheme={progressData.percent >= 100 ? "green" : "blue"}
            bg="#EDF2F7"
            hasStripe={progressData.percent < 100}
            isAnimated={progressData.percent < 100}
          />
          <Text
            fontSize="10px"
            fontWeight="600"
            color="#94A3B8"
            mt="1"
            textAlign="right"
          >
            {progressData.percent.toFixed(1)}%
          </Text>
        </Box>
      )}

      <Box
        position="absolute"
        top="3"
        left="3"
        bg="#fff"
        borderWidth="1px"
        borderColor="#E8EAED"
        borderRadius="10px"
        boxShadow={boxShadow}
        p="2"
        zIndex={14}
      >
        <IconButton
          aria-label=""
          onClick={() => Router.back("/mailing")}
          icon={<ChevronLeftIcon w="18px" h="18px" />}
        />
      </Box>
      <ReactFlow
        nodes={stateNodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.25, maxZoom: 0.75 }}
        nodesDraggable={isEditable}
        nodesConnectable={isEditable}
        elementsSelectable
        zoomOnPinch
        zoomOnDoubleClick={false}
        panOnDrag={isEditable}
        panOnScroll={isEditable}
        deleteKeyCode={["Backspace", "Delete"]}
        onConnect={handleConnect}
        onEdgesDelete={isEditable ? handleDeleteEdges : undefined}
        onNodeClick={handleNodeClick}
        onInit={(instance) => {
          reactFlowInstanceRef.current = instance;
        }}
        onNodeDrag={(_, node) => {
          updateNodePosition(node.id, node.position);
        }}
        onNodeDragStop={(_, node) => {
          updateNodePosition(node.id, node.position, true);
        }}
      >
        <Background gap={18} color="#D5DEE9" />
        <Controls />
      </ReactFlow>

      {isShowPipelineTemplate && (
        <PipelineTemplateGallery
          mailingTipoOperacao={mailingData.tipoOperacao as string | undefined}
          mailingConvenio={mailingData.convenio as string | undefined}
          onApply={(modelo: PipelineModeloDTO) => {
            if (modelo.sequenciaBloco) {
              try {
                const parsed = JSON.parse(modelo.sequenciaBloco);
                parsed.idMailing = mailingData.id;
                const machine = new StatesMachine(parsed);
                nodePositionsRef.current = {};
                hasFittedRef.current = false;
                dispatchPipelineData({ type: "SET", payload: machine });
              } catch {
                // If parsing fails, just dismiss and let user build from scratch
              }
            }
            setPermitedShowTemplate(false);
          }}
          onDismiss={() => setPermitedShowTemplate(false)}
        />
      )}

      <Box
        position="absolute"
        left="50%"
        bottom="0"
        transform="translateX(-50%)"
        w={selectedState ? `calc(100% - 360px)` : "100%"}
      >
        <Flex
          position="absolute"
          left="50%"
          transform="translateX(-50%)"
          bg="#FFFFFF"
          border="1px solid"
          borderColor="#D6DCE6"
          borderRadius="12px"
          boxShadow={boxShadow}
          p="3"
          gap="3"
          zIndex={10}
          bottom="4"
        >
          {!isPipelineCreated && (
            <ButtonWithLoading
              tooltipMessage={
                hasPendingConfigNodes
                  ? "É necessário configurar todos os Blocos"
                  : pipelineData.isInvalidPipeline()
                    ? "Pipeline inválido, verifique as conexões"
                    : undefined
              }
              isLoading={isSaving}
              isDisabled={pipelineData.isInvalidPipeline()}
              onClick={handleCreatePipeline}
              leftIcon={<AddIcon />}
            >
              Criar Pipeline
            </ButtonWithLoading>
          )}

          {mailingData.id && (
            <ButtonWithLoading
              leftIcon={<DownloadIcon />}
              isLoading={isSaving}
              isDisabled={!isPipelineCreated}
              tooltipMessage={
                isPipelineCreated ? undefined : "Pipeline não criado"
              }
              onClick={async () => {
                setIsSaving?.(true);
                try {
                  await PipelineEndpoints.downloadPipelineCSV(mailingData.id!);
                } finally {
                  setIsSaving?.(false);
                }
              }}
            >
              Baixar CSV
            </ButtonWithLoading>
          )}
        </Flex>
      </Box>
    </Box>
  );
}
